// TOBY's own public links — the social profiles, the Play Store and App Store
// listings and the Google review page — as typed access to
// config/brand_links.json.
//
// That JSON is the ONE place a link is stored. The marketing footer renders
// the social profiles as icon links, the homepage Organization JSON-LD
// publishes the same URLs as `sameAs` (the property search engines use to tie
// a profile to the brand), the share caption prints a handle, the Android
// banner, the testimonials and the /app redirect link the store listings, and
// the review-ask mail (ReviewMailer, through config/initializers/brand_links.rb)
// links the Google review page. Edit the JSON and every surface follows;
// nothing here holds a URL of its own.
import brandLinks from "../../../config/brand_links.json"

// `key` selects the brand mark in components/social-links.svelte, whose icon
// map is checked against this list — so a new network needs its icon added
// there before svelte-check passes, and the test beside this file rejects a
// JSON key that is not listed here.
export const SOCIAL_KEYS = [
  "instagram",
  "tiktok",
  "x",
  "linkedin",
  "youtube",
] as const
export type SocialKey = (typeof SOCIAL_KEYS)[number]

export type SocialProfile = {
  key: SocialKey
  /** Accessible name — the network, not the handle. Never translated. */
  name: string
  url: string
}

export const SOCIAL_PROFILES: readonly SocialProfile[] =
  brandLinks.social as SocialProfile[]

/** Plain Play Store listing link — Android hands it to the Play app itself. */
export const PLAY_STORE_URL: string = brandLinks.play_store

/** The App Store listing, null until TOBY is in the App Store. */
export const APP_STORE_URL: string | null = brandLinks.app_store

/**
 * "@toby.site" — a profile's handle, derived from its URL rather than stored
 * beside it. A second field is a second thing to update when a handle moves,
 * and the copy that silently keeps saying the old name is always the one
 * nobody remembers is there — a share caption, say.
 *
 * Empty string for a key with no profile, so a caption degrades to no mention
 * rather than printing "undefined" at someone.
 */
export function socialHandle(key: SocialKey): string {
  const url = SOCIAL_PROFILES.find((profile) => profile.key === key)?.url
  if (!url) return ""

  const slug = new URL(url).pathname.replaceAll("/", "").replace(/^@/, "")
  return slug ? `@${slug}` : ""
}
